import { ChangeDetectionStrategy, Component, inject, signal, TemplateRef, ViewChild } from '@angular/core';


import { RouterLink } from '@angular/router';
import { of } from 'rxjs';
import { DisplayThingie } from 'src/app/components/list-thingies/list-thingies.component';
import { MaterialImportsModule } from 'src/app/material-imports.module';
import { BoldPipeByMatchedPart } from 'src/app/utility/pipes/bold-pipes';
import { DefaultUrls } from 'src/assets/default.urls';
import { DialogControl } from '../../dialog/dialog-control';
import { IIsDialog } from '../../dialog/dialog.service';

type Device = DisplayThingie['assignedDevices'][number];

/**
 * Data to be passed to {@link AssignedDevicesDialogComponent}.
 */
export type IAssignedDevicesDialogData = {
  devices: Device[];

  assignedTo: {
    type: 'thingie';
    name: string;
    id: string;
  };

  /**
   * Optional term to highlight devices in the list, which match the term.
   */
  matchTerm?: string;
};


/**
 * Component to show a dialog with a list of assigned devices.
 *
 * Do not use directly, use the {@link AssignedDevicesDialogService} to open the dialog.
 */
@Component({
  imports: [
    ...MaterialImportsModule,
    RouterLink,
    BoldPipeByMatchedPart
  ],
  template: `
    <!-- content -->
    <div>
      <p>
        <small>
          (Devices assigned to {{assignee().type}}
          <a [routerLink]="defaultUrls.route.thingieDetail(assignee().id)" matTooltip="Go to {{assignee().type}}">
            {{assignee().name}}
          </a>)
        </small>
      </p>


      @if(devices().length === 0) {
        <p>No devices assigned.</p>
      } @else {
        <ul>
          @for(device of devices(); track device.id) {
            <li matTooltip="View device details">
              <a [routerLink]="defaultUrls.route.deviceDetail(device.id)">
                <span [innerHtml]="device.name | boldMatchedPart:matchTerm()"></span>
              </a>
            </li>
          }
        </ul>
      }
    </div>

    <!-- buttons -->
    <ng-template #dialogButtons>
      <button mat-stroked-button (click)="closeDialog()">Close</button>
    </ng-template>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AssignedDevicesDialogComponent implements IIsDialog<IAssignedDevicesDialogData, undefined> {
  public readonly __compileTimeTypeInferenceHelper: [IAssignedDevicesDialogData, undefined][] = [];

  @ViewChild('dialogButtons', { read: TemplateRef, static: true })
  private set dialogButtons(template: TemplateRef<unknown> | null) {
    this.dialogControl.setActionButtonsTemplate(template ?? null);
  }

  public readonly title$ = of('Assigned Devices');

  protected readonly defaultUrls = DefaultUrls;

  private dialogControl = inject<DialogControl<IAssignedDevicesDialogData>>(DialogControl);

  private readonly dialogData = this.dialogControl.data;


  protected readonly devices = signal<Device[]>(this.dialogData.devices).asReadonly();


  protected readonly assignee = signal({
    type: this.convertTypeToName(this.dialogData.assignedTo.type),
    name: this.dialogData.assignedTo.name,
    id: this.dialogData.assignedTo.id,
  }).asReadonly();


  private convertTypeToName(type: IAssignedDevicesDialogData['assignedTo']['type']): string {
    switch (type) {
      case 'thingie':
        return 'replicate';
      default:
        return type;
    }
  }


  protected readonly matchTerm = signal(this.dialogData.matchTerm ?? '').asReadonly();

  protected closeDialog() {
    return this.dialogControl.requestCloseDialog(undefined);
  }

}
